import { useSelector } from 'react-redux';
import { DifferenceProps } from '../DifferenceElementView';
import DifferenceDescription from '../DifferenceDescription/DifferenceDescription';
import { getIsSingleFile } from 'store';
import { DocumentTypes } from 'types';
import DifferenceCardThumbnail, { type DifferenceCardThumbnailStatus } from './DifferenceCardThumbnail';
import DifferenceCardGraphics from './DifferenceCardGraphics';

const MarkupDifference = ({ difference, multipleSelectionMode = false }: DifferenceProps) => {
  const isSingleFile = useSelector(getIsSingleFile);

  const renderContent = (type: DocumentTypes) => {
    const isLoading =
      type === DocumentTypes.source ? difference.sourceThumbnailLoading : difference.targetThumbnailLoading;

    if (!(type in difference) || difference[type].location.rect.every((val) => val === 0)) {
      if (isSingleFile && type === DocumentTypes.source) {
        return null;
      }
      return <DifferenceCardThumbnail status="noComparison" alt="" />;
    }

    const thumbnailUrl = difference.thumbnailPaths?.[type]?.thumbnailUrl;
    let status: DifferenceCardThumbnailStatus = 'ready';
    if (isLoading || thumbnailUrl === undefined) {
      status = 'loading';
    } else if (thumbnailUrl === null) {
      status = 'noComparison';
    }

    return <DifferenceCardThumbnail status={status} src={thumbnailUrl ?? undefined} alt={`${type} thumbnail`} />;
  };

  const hasSourceMarkup = difference.source && difference.source.location && difference.source.location.pageNumber > 0;
  const hasTargetMarkup = difference.target && difference.target.location && difference.target.location.pageNumber > 0;

  if (isSingleFile) {
    return (
      <DifferenceDescription
        difference={difference}
        sourceDescription={renderContent(DocumentTypes.target)}
        targetDescription={null}
        sourcePageNumberText={hasTargetMarkup ? `new (p${difference.target.location.pageNumber})` : ''}
        targetPageNumberText=""
        renderIcon={() => null}
        showExtra={false}
        multipleSelectionMode={multipleSelectionMode}
      />
    );
  }

  return (
    <DifferenceDescription
      difference={difference}
      media={
        <DifferenceCardGraphics
          source={renderContent(DocumentTypes.source)}
          target={renderContent(DocumentTypes.target)}
          sourceLabel={hasSourceMarkup ? `source (p${difference.source.location.pageNumber})` : ''}
          targetLabel={hasTargetMarkup ? `new (p${difference.target.location.pageNumber})` : ''}
        />
      }
      showExtra={false}
      multipleSelectionMode={multipleSelectionMode}
    />
  );
};

export default MarkupDifference;
